<template>
  <b-form class="mb-2 border p-3 rounded-lg" novalidate @submit.prevent="save">
    <b-form-group :label="label" :label-for="inputId" class="mb-2">
      <b-form-textarea
        :id="inputId"
        v-model="valor"
        rows="2"
        :disabled="busy || !canSave"
        :state="v$.$dirty ? !v$.$invalid : null"
        :aria-describedby="`${inputId}-feedback`"
        @blur="v$.$touch()"
      />
      <b-form-invalid-feedback :id="`${inputId}-feedback`">
        Informe uma tradução com até 500 caracteres. O campo não pode conter
        apenas espaços.
      </b-form-invalid-feedback>
    </b-form-group>
    <div class="d-inline-flex w-100">
      <div class="flex-grow-1" />
      <confirm-click
        v-if="saved && canDelete"
        @call="remove"
        @reset="$root.$emit('bv::hide::tooltip')"
        class="mr-2"
      >
        <template #default="{ confirmed }">
          <b-button
            v-b-tooltip.click="{ title: promptToRemove }"
            size="sm"
            :variant="confirmed ? 'danger' : 'outline-danger'"
            :disabled="busy"
          >
            <font-awesome-icon
              v-if="confirmed"
              icon="exclamation"
              class="mr-1"
            />
            Excluir tradução
          </b-button>
        </template>
      </confirm-click>
      <b-button
        v-if="dirty"
        size="sm"
        class="ml-2"
        variant="outline-exitos"
        :disabled="busy"
        @click="undoChanges"
      >
        Desfazer alteração
      </b-button>
      <b-button
        v-if="canSave"
        type="submit"
        size="sm"
        variant="exitos"
        :disabled="busy || !dirty"
      >
        {{ busy ? "Aguarde..." : "Salvar tradução" }}
      </b-button>
    </div>
  </b-form>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import useVuelidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import can from "@/helpers/functions/can";
import { useNotify } from "@/composables/useNotify";
import services from "@/http";
import ConfirmClick from "@/components/ConfirmClick.vue";

const props = defineProps({
  tipo: { type: String, required: true },
  termoId: { type: String, required: true },
  idioma: { type: String, required: true },
  label: { type: String, required: true },
  traducao: { type: Object, default: null },
});

const emit = defineEmits(["state-change"]);

const notifications = useNotify();

const saved = ref(props.traducao ? { ...props.traducao } : null);
const valor = ref(saved.value?.valor || "");
const busy = ref(false);

const inputId = computed(() => `traducao-${props.idioma}`);

const dirty = computed(() => valor.value !== (saved.value?.valor || ""));

const canSave = computed(() =>
  can(saved.value ? "traducoes:edit" : "traducoes:create")
);

const canDelete = computed(() => can("traducoes:delete"));

const promptToRemove = computed(
  () =>
    `Excluir a tradução em ${props.label}? O termo original será exibido neste idioma.`
);

const v$ = useVuelidate(
  { valor: { required, maxLength: maxLength(500) } },
  { valor }
);
watch(
  [dirty, busy],
  () => emit("state-change", { dirty: dirty.value, busy: busy.value }),
  { immediate: true }
);

function undoChanges() {
  valor.value = saved.value?.valor || "";
  v$.value.$reset();
}

async function save() {
  if (busy.value || !canSave.value || !dirty.value) return;

  valor.value = valor.value.trim();
  busy.value = true;
  try {
    if (!(await v$.value.$validate())) return;

    const response = !!saved.value?.id
      ? await services.termos_filtros.update(
          { id: saved.value.id },
          { valor: valor.value }
        )
      : await services.termos_filtros.store({
          traduzivel_type: props.tipo,
          traduzivel_id: String(props.termoId),
          idioma: props.idioma,
          valor: valor.value,
        });

    saved.value = response.data.traducao_catalogo;
    undoChanges();
    notifications.addNotification({
      type: "success",
      message: `Tradução em ${props.label} salva com sucesso.`,
    });
  } catch (error) {
    notifications.addNotification({
      type: "danger",
      message:
        "Não foi possível salvar a tradução. Suas alterações foram mantidas. Tente novamente.",
    });
  } finally {
    busy.value = false;
  }
}

async function remove() {
  if (busy.value || !saved.value || !canDelete.value) return;

  busy.value = true;
  try {
    await services.termos_filtros.delete({ id: saved.value.id });
    saved.value = null;
    undoChanges();
    notifications.addNotification({
      type: "success",
      message: `Tradução em ${props.label} excluída com sucesso.`,
    });
  } catch (error) {
    notifications.addNotification({
      type: "danger",
      message:
        "Não foi possível excluir a tradução. Ela foi mantida. Tente novamente.",
    });
  } finally {
    busy.value = false;
  }
}
</script>
